import type {
  InventoryLifecycleEventSource,
  InventoryLifecycleEventType,
  InventoryState,
  InventoryUsageType,
  WebsiteVisibility,
  WebsiteVisibilityMode,
} from '@jazel-auto/ise-contracts/dist/inventory.js'

export type {
  InventoryLifecycleEventSource,
  InventoryLifecycleEventType,
  InventoryState,
  InventoryUsageType,
  WebsiteVisibility,
  WebsiteVisibilityMode,
}

/**
 * Durable operational ownership mode for inventoryState / inventoryUsageType.
 * AUTO (or missing) = feed-derived; MANUAL = dealer-owned.
 */
export type InventoryOwnershipMode = 'AUTO' | 'MANUAL'

export type WebsiteVisibilityExplanation = { category: 'inventory_state_policy' | 'manual' | 'operational' | 'publish_rule' | 'unknown'; evidenceBasis: 'recorded' | 'current_policy' | 'unknown'; reason?: string; inventoryState?: string }

export interface InventoryVehicle {
  websiteVisibilityExplanation?: WebsiteVisibilityExplanation
  vin: string
  year?: number
  make?: string
  model?: string
  trim?: string
  stock_number?: string
  price?: number
  condition?: string
  feedType: 'manual' | 'auto'
  source?: 'manual' | 'auto' // Alias for feedType or additional property
  inventoryState?: InventoryState
  previousInventoryState?: InventoryState
  inventoryUsageType?: InventoryUsageType
  inventoryStateMode?: InventoryOwnershipMode
  inventoryStateChangedAt?: string
  inventoryStateChangedBy?: string
  inventoryUsageTypeMode?: InventoryOwnershipMode
  inventoryUsageTypeChangedAt?: string
  inventoryUsageTypeChangedBy?: string
  websiteVisibility?: WebsiteVisibility
  websiteVisibilityMode?: WebsiteVisibilityMode
  websiteVisibilityReason?: string
  websiteVisibilityChangedAt?: string
  websiteVisibilityChangedBy?: string
  // Dealer inventory date (Unix timestamp in seconds)
  date_in_stock_ts?: number
  // Current/removed inventory dates
  addedDate?: string // ISO timestamp when vehicle was first added to inventory
  firstActiveAt?: string
  archivedAt?: string
  archivedBy?: string
  archivedSource?: string
  archiveReason?: string
  feedSource?: string
  feedSources?: string[]
  removedAt?: string
  removedReason?: string
  /** @deprecated Prefer removedAt/archivedAt or websiteVisibilityChangedAt depending on context. */
  delistedDate?: string
  /** @deprecated Prefer removedReason/archiveReason or websiteVisibilityReason depending on context. */
  delistReason?: string
  membershipStatus?: 'CURRENT' | 'REMOVED' // Whether vehicle is currently in inventory or was removed
  /**
   * Publish state: 'draft' = saved but not live on website; 'published' = live on website/OpenSearch.
   * Missing/undefined = 'published' for backward compatibility.
   */
  publishState?: 'draft' | 'published'
  /** User-controlled tags (controlled vocabulary). Multi-select, max 10. */
  tags?: string[]
  /** System-derived tags (auto-computed: RECENTLY_ADDED, AGED_30_PLUS, etc.). Read-only. */
  systemTags?: string[]
}

export interface InventoryResponse {
  jazelId: string
  vehicles: InventoryVehicle[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
  message?: string
}

export type ManualOverrideReleasePolicy =
  | 'UNTIL_SOLD'
  | 'UNTIL_FEED_VALUE_CHANGES'
  | 'MANUAL_RELEASE_ONLY'
  | 'UNTIL_DATE'

/** Normalized, UI-safe view of a committed ACTIVE manual override (from vehicle-by-vin). */
export interface ActiveManualOverride {
  field: string
  manualValue: unknown
  mappedValueAtLock: unknown
  /** Current feed/base value the field would return to if this override is released. */
  valueIfReleasedNow?: unknown
  /** Whether release preview resolved to a value, known empty, or could not be resolved. */
  releasePreviewStatus?: 'resolved' | 'empty' | 'unavailable'
  /** Backward-compatible alias for valueIfReleasedNow. */
  currentFeedValue?: unknown
  currentMappingSourceFeed?: string
  currentMappingSourceField?: string
  currentMappingSourcePath?: string
  releasePolicy: ManualOverrideReleasePolicy
  expiresAt?: string
  lockedAt: string
  lockedBy: string
  sourceFeed: string
  sourceField: string
  sourcePath: string
}

export interface GetVehicleByVinResponse {
  jazelId: string
  vin: string
  vehicle: Record<string, unknown> | null
  feedType?: 'manual' | 'auto'
  feedSources?: string[]
  /** Feed-backed only: committed ACTIVE manual overrides for this VIN. */
  manualOverrides?: { active: ActiveManualOverride[] }
  eventType?: InventoryLifecycleEventType
  inventoryStateMode?: InventoryOwnershipMode
  inventoryStateChangedAt?: string
  inventoryStateChangedBy?: string
  inventoryUsageTypeMode?: InventoryOwnershipMode
  inventoryUsageTypeChangedAt?: string
  inventoryUsageTypeChangedBy?: string
  websiteVisibility?: WebsiteVisibility
  websiteVisibilityMode?: WebsiteVisibilityMode
  websiteVisibilityReason?: string
  websiteVisibilityChangedAt?: string
  archivedAt?: string
  archiveReason?: string
  removedAt?: string
  removedReason?: string
  /** @deprecated Prefer removedReason/archiveReason or websiteVisibilityReason depending on context. */
  delistReason?: string
  membershipStatus?: 'CURRENT' | 'REMOVED'
  /** @deprecated Prefer removedAt/archivedAt or websiteVisibilityChangedAt depending on context. */
  delistedDate?: string
  publishState?: 'draft' | 'published' // 'draft' = not live, 'published' = live on website
  /**
   * Manual vehicles only: when true, a feed containing this VIN may reclaim it back to feed-backed.
   * Set automatically for absent-from-feed restore fallbacks; toggleable for manual vehicles.
   */
  allowFeedReclaim?: boolean
  message?: string
}

export type VehicleOpenSearchStatusResponse = {
  jazelId: string
  vin: string
  found: boolean | null
  checkedAt: string
}

export type VehicleStatusHistoryItem = {
  id: string
  jazelId: string
  vin: string
  eventType: InventoryLifecycleEventType
  createdAt: string
  timestamp?: string
  createdBy?: string
  previousInventoryState?: InventoryState
  nextInventoryState?: InventoryState
  previousInventoryUsageType?: InventoryUsageType
  nextInventoryUsageType?: InventoryUsageType
  previousWebsiteVisibility?: WebsiteVisibility
  nextWebsiteVisibility?: WebsiteVisibility
  reason?: string
  correlationId?: string
  source?: InventoryLifecycleEventSource
  sourceFeed?: string
  metadata?: Record<string, unknown>
}

export type VehicleStatusHistoryResponse = {
  jazelId: string
  vin: string
  items: VehicleStatusHistoryItem[]
  truncated: boolean
}

export interface InventoryQuery {
  jazelId: string
  limit?: number
  offset?: number
  type?: 'manual' | 'auto'
  condition?: string
  segment?: string
  vin?: string
  includeArchived?: boolean // Include vehicles archived from active inventory
  q?: string // Search term (VIN, make/model, trim, stock #)
  websiteVisibility?: 'all' | WebsiteVisibility
  inventoryState?: 'all' | InventoryState
  inventoryUsageType?: 'all' | InventoryUsageType
  /** Filter to vehicles that have this tag (user or system tag value, e.g. 'HOT'). */
  tag?: string
  sortBy?:
    | 'year'
    | 'make'
    | 'model'
    | 'price'
    | 'days_in_inventory'
  sortDirection?: 'asc' | 'desc'
}

export interface DraftSummaryResponse {
  jazelId: string
  draftCount: number
}

export type DraftSummaryByIntent = {
  CREATE: number
  PATCH: number
  DELETE: number
  RESTORE: number
}

export type VehicleDraftIntent = 'CREATE' | 'PATCH' | 'DELETE' | 'RESTORE'

export type VehicleDraftListItem = {
  vin: string
  updatedAt: string
  fieldKeys?: string[]
  operationalFieldKeys?: string[]
  /** Staged manual override action field keys (feed-backed vehicle drafts). */
  manualOverrideActionKeys?: string[]
  manualOverrideActionCount?: number
  manualOverrideActionSummary?: Array<{ field: string; action: string }>
  intent?: VehicleDraftIntent
  deleteRequestedAt?: string
  deleteRequestedBy?: { username: string }
}

export interface ListVehicleDraftsResponse {
  jazelId: string
  drafts: VehicleDraftListItem[]
}

export const resolveVehicleDraftIntent = (intent?: VehicleDraftIntent | null): VehicleDraftIntent => {
  if (intent === 'CREATE' || intent === 'DELETE' || intent === 'RESTORE') return intent
  return 'PATCH'
}

export type VehicleDraftIntentChip = {
  label: string
  color: 'primary' | 'error' | 'success' | 'info'
}

export const getVehicleDraftIntentChip = (
  intent?: VehicleDraftIntent | null,
): VehicleDraftIntentChip | null => {
  switch (resolveVehicleDraftIntent(intent)) {
    case 'DELETE':
      return { label: 'Will remove', color: 'error' }
    case 'RESTORE':
      return { label: 'Will restore', color: 'success' }
    case 'PATCH':
      return { label: 'Will update', color: 'primary' }
    case 'CREATE':
      return { label: 'Will create', color: 'info' }
    default:
      return null
  }
}

export const formatVehicleDraftIntentChipLabel = (
  intent?: VehicleDraftIntent | null,
  fieldCount = 0,
): string | null => {
  const chip = getVehicleDraftIntentChip(intent)
  if (!chip) return null

  const count = Number.isFinite(fieldCount) && fieldCount > 0 ? Math.floor(fieldCount) : 0
  const resolved = resolveVehicleDraftIntent(intent)
  if (count > 0 && resolved !== 'DELETE' && resolved !== 'RESTORE') {
    return `${chip.label} · ${count} field${count === 1 ? '' : 's'}`
  }

  return chip.label
}

export const summarizeDraftsByIntent = (drafts: VehicleDraftListItem[]): DraftSummaryByIntent => {
  const byIntent: DraftSummaryByIntent = { CREATE: 0, PATCH: 0, DELETE: 0, RESTORE: 0 }
  for (const draft of drafts) {
    const intent = resolveVehicleDraftIntent(draft.intent)
    byIntent[intent] += 1
  }
  return byIntent
}

export type PublishRunMetrics = {
  feed: { added: number; removed: number; changed: number; listed: number; delisted: number }
  manual: { added: number; removed: number; changed: number; listed: number; delisted: number }
  truncated: boolean
  vinsTruncated?: boolean
  vins?: {
    feed: { added: string[]; removed: string[]; changed: string[]; listed: string[]; delisted: string[] }
    manual: { added: string[]; removed: string[]; changed: string[]; listed: string[]; delisted: string[] }
  }
}

export interface PublishRunMetricsResponse {
  success: boolean
  jazelId: string
  correlationId: string
  metrics: PublishRunMetrics
}

export const fetchPublishRunMetrics = async (
  endpoint: string,
  jazelId: string,
  correlationId: string,
  opts?: { includeVins?: boolean; maxVins?: number },
): Promise<PublishRunMetricsResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/inventory`)
  url.searchParams.set('action', 'runMetrics')
  url.searchParams.set('jazelId', jazelId)
  url.searchParams.set('correlationId', correlationId)
  if (opts?.includeVins) url.searchParams.set('includeVins', 'true')
  if (typeof opts?.maxVins === 'number' && opts.maxVins > 0) url.searchParams.set('maxVins', String(opts.maxVins))

  const response = await fetch(url.toString())
  if (!response.ok) {
    throw new Error('Failed to load publish run metrics')
  }

  return response.json()
}

export const fetchInventory = async (
  endpoint: string,
  query: InventoryQuery,
): Promise<InventoryResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/inventory`)

  url.searchParams.set('jazelId', query.jazelId)
  if (query.limit != null) url.searchParams.set('limit', String(query.limit))
  if (query.offset != null) url.searchParams.set('offset', String(query.offset))
  if (query.type) url.searchParams.set('type', query.type)
  if (query.condition && query.condition !== 'all') {
    url.searchParams.set('condition', query.condition)
  }
  if (query.segment && query.segment !== 'all') {
    url.searchParams.set('segment', query.segment)
  }
  if (query.includeArchived) {
    url.searchParams.set('includeArchived', 'true')
  }
  if (query.q) {
    url.searchParams.set('q', String(query.q))
  }
  if (query.vin) {
    url.searchParams.set('vin', query.vin)
  }
  if (query.websiteVisibility && query.websiteVisibility !== 'all') {
    url.searchParams.set('websiteVisibility', query.websiteVisibility)
  }
  if (query.inventoryState && query.inventoryState !== 'all') {
    url.searchParams.set('inventoryState', query.inventoryState)
  }
  if (query.inventoryUsageType && query.inventoryUsageType !== 'all') {
    url.searchParams.set('inventoryUsageType', query.inventoryUsageType)
  }
  if (query.tag) {
    url.searchParams.set('tag', query.tag)
  }
  if (query.sortBy) {
    url.searchParams.set('sortBy', query.sortBy)
    if (query.sortDirection) {
      url.searchParams.set('sortDirection', query.sortDirection)
    }
  }

  const response = await fetch(url.toString())
  if (!response.ok) {
    throw new Error('Failed to load inventory')
  }

  return response.json()
}

/**
 * Fetch draft summary (count of vehicles with pending unpublished changes).
 * Uses the VehicleDraft table (not CurrentInventory.publishState).
 */
export const fetchDraftSummary = async (
  endpoint: string,
  jazelId: string,
  sessionToken?: string,
): Promise<DraftSummaryResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts/summary`)
  url.searchParams.set('jazelId', jazelId)

  const headers: Record<string, string> = {}
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), { headers })
  if (!response.ok) {
    throw new Error('Failed to load draft summary')
  }

  return response.json()
}

export interface VehicleDraftFieldEntry {
  value: unknown
  updatedAt: string
  changedBy: { username: string }
}

export interface VehicleDraftRecord {
  jazelId: string
  vin: string
  fields: Record<string, VehicleDraftFieldEntry>
  operationalPatch?: {
    inventoryState?: InventoryState
    inventoryStateMode?: InventoryOwnershipMode
    inventoryUsageType?: InventoryUsageType
    inventoryUsageTypeMode?: InventoryOwnershipMode
    websiteVisibility?: WebsiteVisibility
    websiteVisibilityMode?: WebsiteVisibilityMode
    websiteVisibilityReason?: string
  }
  /** Feed-backed only: staged manual override actions, keyed by field (committed at publish). */
  manualOverrideActions?: Record<string, PendingManualOverrideAction>
  updatedAt: string
  baseRef?: Record<string, unknown>
  intent?: VehicleDraftIntent
  deleteRequestedAt?: string
  deleteRequestedBy?: { username: string }
}

/** A staged (pending) manual override action read back from the draft. */
export interface PendingManualOverrideAction {
  action: 'ADD_OVERRIDE' | 'UPDATE_OVERRIDE' | 'REMOVE_OVERRIDE'
  manualValue?: unknown
  mappedValueAtLock?: unknown
  releasePolicy?: ManualOverrideReleasePolicy
  expiresAt?: string
  sourceFeed?: string
  sourceField?: string
  sourcePath?: string
  updatedAt?: string
}

export interface GetVehicleDraftResponse {
  jazelId: string
  vin: string
  draft: VehicleDraftRecord | null
}

export type RevertVehicleDraftResponse = {
  success: boolean
  message?: string
  jazelId: string
  vin: string
  deletedDraft?: boolean
}

export type StageVehicleDeleteDraftResponse = {
  success: boolean
  message?: string
  jazelId: string
  vin: string
  intent?: 'DELETE'
  created?: boolean
  previousIntent?: VehicleDraftIntent | null
}

export type CancelVehicleDeleteDraftResponse = {
  success: boolean
  message?: string
  jazelId: string
  vin: string
  intent?: 'PATCH'
  existed?: boolean
  previousIntent?: VehicleDraftIntent | null
}

export const stageVehicleDeleteDraft = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  sessionToken?: string,
): Promise<StageVehicleDeleteDraftResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts/stage-delete`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ jazelId, vin }),
  })
  const json = (await response.json().catch(() => ({}))) as StageVehicleDeleteDraftResponse
  if (!response.ok) {
    throw new Error(json?.message || 'Failed to stage vehicle deletion')
  }
  return json
}

export const cancelVehicleDeleteDraft = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  sessionToken?: string,
): Promise<CancelVehicleDeleteDraftResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts/cancel-delete`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ jazelId, vin }),
  })
  const json = (await response.json().catch(() => ({}))) as CancelVehicleDeleteDraftResponse
  if (!response.ok) {
    throw new Error(json?.message || 'Failed to cancel vehicle deletion')
  }
  return json
}

export type StageVehicleArchiveOperationalPatchResponse = {
  success: boolean
  message?: string
  jazelId: string
  vin: string
  intent?: VehicleDraftIntent
}

/**
 * Stages an archive for a feed-backed vehicle as an operational patch draft
 * (intent=PATCH, operationalPatch.inventoryState=ARCHIVED, inventoryStateMode=MANUAL).
 * Feed-backed archive publishes through the feed-backed operational-only ARCHIVED path,
 * so it must not be routed through the intent=DELETE flow used by manual vehicles.
 */
export const stageVehicleArchiveOperationalPatch = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  sessionToken?: string,
): Promise<StageVehicleArchiveOperationalPatchResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      jazelId,
      vin,
      operationalPatch: {
        inventoryState: 'ARCHIVED' as InventoryState,
        inventoryStateMode: 'MANUAL' as InventoryOwnershipMode,
      },
    }),
  })
  const json = (await response.json().catch(() => ({}))) as StageVehicleArchiveOperationalPatchResponse
  if (!response.ok) {
    throw new Error(json?.message || 'Failed to stage vehicle archive')
  }
  return json
}

/**
 * Immediately updates a MANUAL vehicle's allowFeedReclaim flag (no draft, no publish). When true, a
 * feed containing this VIN may reclaim it back to feed-backed. Rejected server-side for feed-backed
 * (type='auto') vehicles.
 */
export const updateAllowFeedReclaim = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  allowFeedReclaim: boolean,
  sessionToken?: string,
): Promise<{ jazelId: string; vin: string; allowFeedReclaim: boolean; message?: string }> => {
  const base = endpoint.replace(/\/+$/g, '')
  // Served as an action on the existing inventory_api route (POST /inventory) rather than a
  // dedicated Lambda/route. The handler dispatches on body.action.
  const url = new URL(`${base}/inventory`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'updateAllowFeedReclaim', jazelId, vin, allowFeedReclaim }),
  })
  const json = (await response.json().catch(() => ({}))) as {
    jazelId: string
    vin: string
    allowFeedReclaim: boolean
    message?: string
    error?: string
    details?: string
  }
  if (!response.ok) {
    throw new Error(json?.details || json?.error || json?.message || 'Failed to update allowFeedReclaim')
  }
  return json
}

export const fetchVehicleDraftList = async (
  endpoint: string,
  jazelId: string,
  sessionToken?: string,
): Promise<ListVehicleDraftsResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts`)
  url.searchParams.set('jazelId', jazelId)

  const headers: Record<string, string> = {}
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), { headers })
  if (!response.ok) {
    throw new Error('Failed to load vehicle drafts')
  }

  const json = (await response.json()) as ListVehicleDraftsResponse
  return {
    jazelId: json.jazelId,
    drafts: Array.isArray(json.drafts) ? json.drafts : [],
  }
}

export const fetchVehicleDraft = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  sessionToken?: string,
): Promise<GetVehicleDraftResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts`)
  url.searchParams.set('jazelId', jazelId)
  url.searchParams.set('vin', vin)

  const headers: Record<string, string> = {}
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), { headers })
  if (!response.ok) {
    throw new Error('Failed to load vehicle draft')
  }

  return response.json()
}

export const revertVehicleDraft = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  sessionToken?: string,
): Promise<RevertVehicleDraftResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts/revert`)
  url.searchParams.set('jazelId', jazelId)
  url.searchParams.set('vin', vin)

  const headers: Record<string, string> = {}
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), {
    method: 'DELETE',
    headers,
  })

  const json = (await response.json().catch(() => ({}))) as RevertVehicleDraftResponse
  if (!response.ok) {
    throw new Error(json?.message || 'Failed to revert vehicle draft edits')
  }

  return json
}

/**
 * Reverts a single staged (pending) manual override action from a feed-backed vehicle's draft.
 * Used when the user discards an override change that was already saved as a draft action.
 */
export const revertManualOverrideDraftAction = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  field: string,
  sessionToken?: string,
): Promise<RevertVehicleDraftResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts/manual-override-action`)
  url.searchParams.set('jazelId', jazelId)
  url.searchParams.set('vin', vin)
  url.searchParams.set('field', field)

  const headers: Record<string, string> = {}
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), {
    method: 'DELETE',
    headers,
  })

  const json = (await response.json().catch(() => ({}))) as RevertVehicleDraftResponse
  if (!response.ok) {
    throw new Error(json?.message || 'Failed to revert pending override')
  }

  return json
}

export type RevertVehicleDraftFieldResponse = {
  success: boolean
  message?: string
  jazelId: string
  vin: string
  fieldKey: string
  existed?: boolean
  deletedDraft?: boolean
}

export const revertVehicleDraftField = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  fieldKey: string,
  sessionToken?: string,
): Promise<RevertVehicleDraftFieldResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-drafts/field`)
  url.searchParams.set('jazelId', jazelId)
  url.searchParams.set('vin', vin)
  url.searchParams.set('fieldKey', fieldKey)

  const headers: Record<string, string> = {}
  if (sessionToken) headers.authorization = `Bearer ${sessionToken}`

  const response = await fetch(url.toString(), {
    method: 'DELETE',
    headers,
  })

  const json = (await response.json().catch(() => ({}))) as RevertVehicleDraftFieldResponse
  if (!response.ok) {
    throw new Error(json?.message || 'Failed to revert vehicle draft field')
  }

  return json
}

export const fetchVehicleByVin = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  includeArchived?: boolean,
): Promise<GetVehicleByVinResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/inventory`)
  url.searchParams.set('jazelId', jazelId)
  url.searchParams.set('vin', vin)
  if (includeArchived) {
    url.searchParams.set('includeArchived', 'true')
  }

  const response = await fetch(url.toString())
  if (response.status === 404) {
    return {
      jazelId,
      vin,
      vehicle: null,
      feedType: 'manual',
      message: 'Vehicle not found',
    }
  }
  if (!response.ok) throw new Error('Failed to load vehicle details')
  return response.json()
}

export const fetchVehicleOpenSearchStatus = async (
  endpoint: string,
  jazelId: string,
  vin: string,
): Promise<VehicleOpenSearchStatusResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-opensearch-status`)
  url.searchParams.set('jazelId', jazelId)
  url.searchParams.set('vin', vin)

  const response = await fetch(url.toString())
  if (!response.ok) {
    throw new Error('Failed to load OpenSearch status')
  }
  return response.json()
}

export interface ToggleWebsiteVisibilityResponse {
  success: boolean
  jazelId: string
  vin: string
  websiteVisibility: WebsiteVisibility
  websiteVisibilityMode?: WebsiteVisibilityMode
  reason?: string | null
  changedAt?: string
  openSearchUpdated?: boolean
  message?: string
}

export const toggleWebsiteVisibility = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  websiteVisibility: WebsiteVisibility,
  reason?: string,
  changedBy?: string,
  websiteVisibilityMode: WebsiteVisibilityMode = 'MANUAL',
  sessionToken?: string,
): Promise<ToggleWebsiteVisibilityResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/website-visibility`)

  // The session token lets the backend attribute the change to the signed-in user.
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      jazelId,
      vin,
      websiteVisibility,
      websiteVisibilityMode,
      reason,
      changedBy,
    }),
  })

  const json = (await response.json().catch(() => ({}))) as ToggleWebsiteVisibilityResponse
  if (!response.ok) {
    throw new Error(json?.message || `Failed to update website visibility (${response.status})`)
  }
  return json
}

export const returnWebsiteVisibilityToAuto = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  changedBy?: string,
  sessionToken?: string,
): Promise<ToggleWebsiteVisibilityResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/website-visibility`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      jazelId,
      vin,
      websiteVisibilityMode: 'AUTO',
      changedBy,
    }),
  })

  const json = (await response.json().catch(() => ({}))) as ToggleWebsiteVisibilityResponse
  if (!response.ok) {
    throw new Error(json?.message || `Failed to return website visibility to automatic (${response.status})`)
  }
  return json
}

export const fetchVehicleStatusHistory = async (
  endpoint: string,
  jazelId: string,
  vin: string,
): Promise<VehicleStatusHistoryResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/vehicle-status-history`)
  url.searchParams.set('jazelId', jazelId)
  url.searchParams.set('vin', vin)

  const response = await fetch(url.toString())
  if (!response.ok) {
    throw new Error('Failed to load vehicle status history')
  }
  return response.json()
}

// ── Tags ─────────────────────────────────────────────────────────────────────

export interface UpdateVehicleTagsResponse {
  success: boolean
  jazelId: string
  vin: string
  tags: string[]
  action: 'set' | 'add' | 'remove'
  openSearchUpdated?: boolean
  message?: string
}

/**
 * Update user-controlled tags on a single vehicle.
 * action:
 *   'set'    — replace all tags with provided list
 *   'add'    — merge into existing (capped at 10)
 *   'remove' — subtract from existing
 */
export const updateVehicleTags = async (
  endpoint: string,
  jazelId: string,
  vin: string,
  tags: string[],
  action: 'set' | 'add' | 'remove' = 'set',
  changedBy?: string,
): Promise<UpdateVehicleTagsResponse> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/tags`)

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jazelId, vin, action, tags, changedBy }),
  })

  const json = (await response.json().catch(() => ({}))) as UpdateVehicleTagsResponse
  if (!response.ok) {
    throw new Error(json?.message || `Failed to update tags (${response.status})`)
  }
  return json
}
